import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_TOKEN } from 'constants/AuthConstant';
import AuthService from 'services/AuthService';
import UserStorageService from 'services/UserStorageService';
import { API_BASE_URL } from 'configs/AppConfig';
import { message } from 'antd';

export const initialState = {
	loading: false,
	message: '',
	showMessage: false,
	redirect: '',
	token: UserStorageService.getToken() || null,
};

export const signIn = createAsyncThunk(
	'auth/login',
	async (data, { rejectWithValue }) => {
		const getAuthorization = await fetch(`${API_BASE_URL}/auth/local`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				identifier: data.email,
				password: data.password,
			}),
		});

		try {
			const responseJSON = await getAuthorization.json();

			if (responseJSON['jwt'] !== undefined) {
				const token = responseJSON['jwt'];
				return { token };
			} else {
				return rejectWithValue(
					responseJSON.error.message === 'Invalid identifier or password'
						? 'Usuario o contraseña incorrectos'
						: responseJSON.error.message
				);
			}
		} catch (err) {
			console.log('err try: ', err);
		}
	}
);

export const signUp = createAsyncThunk(
	'auth/register',
	async (data, { rejectWithValue }) => {
		try {
			const response = await AuthService.register(data);
			return response;
		} catch (err) {
			return rejectWithValue(err.response?.data?.error?.message || 'Error');
		}
	}
);

export const passwordLessSendLink = createAsyncThunk(
	'auth/passwordLessSendLink',
	async (data, { rejectWithValue }) => {
		message.info('Enviando enlace de inicio de sesión...');
		try {
			const response = await AuthService.passwordLessSendLink(data);
			message.success('Enlace de inicio de sesión enviado');
			return response;
		} catch (err) {
			message.error('Error al enviar enlace de inicio de sesión');
			return rejectWithValue(err.response?.data?.error?.message || 'Error');
		}
	}
);

export const passwordLessLogin = createAsyncThunk(
	'auth/passwordLessLogin',
	async (data, { rejectWithValue }) => {
		message.info('Iniciando sesión...');
		try {
			const response = await AuthService.passwordLessLogin(data);

			if (response && response.jwt) {
				const token = response.jwt;
				localStorage.setItem(AUTH_TOKEN, token);
				return { token };
			} else {
				message.error('Token inválido');
				return rejectWithValue('Token inválido');
			}
		} catch (err) {
			UserStorageService.clearUser();
			message.error('Error al iniciar sesión');
			return rejectWithValue(err.response?.data?.error?.message);
		}
	}
);

export const sendVerificationCode = createAsyncThunk(
	'auth/sendVerificationCode',
	async (data, { rejectWithValue }) => {
		try {
			const response = await AuthService.sendVerificationCode(data);
			return response;
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error');
		}
	}
);

export const signOut = createAsyncThunk('auth/logout', async () => {
	UserStorageService.clearUser();
});

export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		authenticated: (state, action) => {
			state.loading = false;
			state.redirect = '/';
			state.token = action.payload;
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload;
			state.showMessage = true;
			state.loading = false;
		},
		hideAuthMessage: state => {
			state.message = '';
			state.showMessage = false;
		},
		signOutSuccess: state => {
			state.loading = false;
			state.token = null;
			state.redirect = '/';
		},
		showLoading: state => {
			state.loading = true;
		},
		signInSuccess: (state, action) => {
			state.loading = false;
			state.token = action.payload;
		},
	},
	extraReducers: builder => {
		builder
			.addCase(signIn.pending, state => {
				state.loading = true;
			})
			.addCase(signIn.fulfilled, (state, action) => {
				state.loading = false;
				state.redirect = '/';
				state.token = action.payload;
			})
			.addCase(signIn.rejected, (state, action) => {
				state.message = action.payload;
				state.showMessage = true;
				state.loading = false;
			})
			.addCase(passwordLessSendLink.pending, state => {
				state.loading = true;
			})
			.addCase(passwordLessSendLink.fulfilled, (state, action) => {
				state.loading = false;
			})
			.addCase(passwordLessSendLink.rejected, (state, action) => {
				state.message = action.payload;
				state.showMessage = true;
				state.loading = false;
			})
			.addCase(passwordLessLogin.pending, state => {
				state.loading = true;
			})
			.addCase(passwordLessLogin.fulfilled, (state, action) => {
				state.loading = false;
				state.redirect = '/';
				state.token = action.payload.token;
			})
			.addCase(passwordLessLogin.rejected, (state, action) => {
				state.message = action.payload;
				state.showMessage = true;
				state.loading = false;
			})
			.addCase(signOut.fulfilled, state => {
				state.loading = false;
				state.token = null;
				state.redirect = '/';
			})
			.addCase(signOut.rejected, state => {
				state.loading = false;
				state.token = null;
				state.redirect = '/';
			})
			.addCase(signUp.pending, state => {
				state.loading = true;
			})
			.addCase(signUp.fulfilled, (state, action) => {
				state.loading = false;
				state.redirect = '/auth/login';
			})
			.addCase(signUp.rejected, (state, action) => {
				state.message = action.payload;
				state.showMessage = true;
				state.loading = false;
			});
	},
});

export const {
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess,
} = authSlice.actions;

export default authSlice.reducer;
